import React, {useEffect, useState} from "react";
import "../styles/tracking.scss";
import {api, getToken, getTrackingInfo} from "./api";
import {Chip, CircularProgress, Grid, Grow, TextField, Typography} from "@mui/material";
import i18next, {t} from "i18next";
import {useDispatch, useSelector} from "react-redux";
import {OrderDto} from "./models";
import Moment from "moment";
import {setToken} from "./store/userReducer";

export const stageColor = (stageColor?: string) => {
  const color = stageColor ?? "#000000";
  const backgroundColor = color + "25";
  return {
    backgroundColor,
    color,
  };
};

function Tracking() {
  const dispatch = useDispatch();
  const lang = i18next.language;
  const token = useSelector((state: any) => state.user.accessToken);

  const [trackingNumber, setTrackingNumber] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNothingFound, setIsNothingFound] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isFromQuery, setIsFromQuery] = useState<boolean>(false);


  const [order, setOrder] = useState<OrderDto | null>(null);
  const searchParams = new URLSearchParams(window.location.search);

  const eventTranslationKey = `event_translation_${lang}`;

  const handleSubmit = (trackingNumber: string) => {
    setIsLoading(true);
    setIsNothingFound(false);
    setIsError(false);
    setIsFromQuery(false);

    if (!trackingNumber) {
      setIsLoading(false);
      return;
    }

    getTrackingInfo(trackingNumber)
      .then((res) => {
        if (res.data) {
          setIsNothingFound(false);
          setOrder(res.data);
        } else {
          setOrder(null);
          setIsNothingFound(true);
        }
      })
      .catch((err) => {
        console.log(err);
        setIsNothingFound(false);
        setIsError(true);
        setOrder(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const formatTrackingNumber = (trackingNumber: string) => {
    return trackingNumber?.replaceAll(/([^a-zA-Z0-9]|\s)*/gi, "")?.toUpperCase()?.slice(0, 20) ?? "";
  };

  const handleChangeTrackingNumber = (trackingNumber: string) => {
    const result = formatTrackingNumber(trackingNumber);
    setTrackingNumber(result);
  };

  useEffect(() => {
    const trackingNumber = searchParams.get("tracknum");
    if (trackingNumber) {
      setIsFromQuery(true);
      handleChangeTrackingNumber(trackingNumber);
      const url = new URL(window.location.href);
      url.search = "";
      const newUrl = url.toString();
      window.history.replaceState({}, document.title, newUrl);
    }

  }, []);


  useEffect(() => {
    if (!token)
      getToken().then((response) => {
        const t = [response?.data?.["token_type"], response?.data?.["access_token"]].join(" ");
        dispatch(setToken(t));
        api.defaults.headers.common["Authorization"] = t;
      }).catch((error) => console.log(error));

  }, [token]);

  useEffect(() => {
    if (token && trackingNumber && isFromQuery) {
      setIsFromQuery(false);
      handleSubmit(formatTrackingNumber(trackingNumber));
    }
  }, [trackingNumber, token, isFromQuery]);

  return (
    <Grid className={"tracking"}>
      <Grid container columns={12} display={"flex"} sx={{
        width: "100%",
        padding: "24px",
        justifyContent: "space-between",
        borderRadius: 4,
        backgroundColor: "var(--color-content-link)!important",
      }}>
        <Grid item xs={12} md={10} paddingRight={{xs: 0, md: 4}} paddingBottom={{xs: 3, md: 0}}>
          <TextField fullWidth value={trackingNumber} variant={"outlined"} placeholder={t("inputPlaceholder")}
                     onChange={(event) => {
                       setIsFromQuery(false);
                       handleChangeTrackingNumber(event.target.value);
                     }}
                     onKeyDown={(event) => {
                       if (event.key === "Enter") {
                         setIsFromQuery(false);
                         handleSubmit(trackingNumber);
                       }
                     }} />
        </Grid>
        <Grid item xs={12} md={2}>
          <button className={"w-btn us-btn-style_1"}
                  style={{width: "100%", height: "48px", cursor: "pointer"}}
                  onClick={() => handleSubmit(trackingNumber)}
                  disabled={isLoading}>
            {isLoading ?
              <CircularProgress sx={{height: "20px", width: "20px"}}
                                size={"sm"} /> : t("track")}
          </button>
        </Grid>
      </Grid>
      <Grow in={!isLoading}>
        <Grid>
          {order && <Grid mt={{md: 8, xs: 3}} display={"flex"} container columns={12}>
            <Grid item md={5} xs={12} display={"flex"} justifyContent={"space-between"} alignItems={"start"}
                  flexWrap={"wrap"}
                  rowGap={"10px"} height={"min-content"}>

              <Grid xs={12} display={"flex"} flexDirection={"row"} alignItems={"center"}>
                <Grid><Typography component={"h4"}>
                  <Typography component={"span"} display={"inline-block"} color={"#000000"}>
                    {t("trackingNumber")}:&nbsp;
                  </Typography>

                  <Typography
                    color={"var(--color-content-link)!important"}
                    component={"span"} fontWeight={"600"} display={"inline-block"}>
                    {order.trackingNumber}
                  </Typography>
                </Typography></Grid>
                <Chip
                  label={
                    order.orderStatus?.customValues[`portal_name_${lang}`] ?? order.orderStatus?.orderStatusDescription ?? ""
                  }
                  variant="outlined"
                  sx={{
                    minHeight: "24px",
                    height: "max-content",
                    fontWeight: 500,
                    fontSize: "14px",
                    border: 0,
                    padding: "0 6px",
                    marginLeft: "1rem",
                    ...stageColor(order.orderStatus?.customValues != null
                    && "portal_stage_color" in order.orderStatus?.customValues
                    && order.orderStatus?.customValues["portal_stage_color"]
                      ? order.orderStatus?.customValues["portal_stage_color"]
                      : "#6C757D"),
                  }}
                />
              </Grid>

              <Grid xs={12} display={"flex"} flexDirection={"column"} rowGap={"10px"}>

                {order?.customValues && "shipped_date" in order?.customValues ?
                  <Grid>
                    <Typography p={0} component={"span"}
                                fontWeight={500}
                                whiteSpace={"nowrap"}
                                color={"#000000"}>
                      {t("event.shipped")}: {Moment(order.customValues["shipped_date"]).format("L")}
                    </Typography>
                  </Grid> : null}

                {order?.customValues && "delivered_date" in order?.customValues ?
                  <Grid>
                    <Typography p={0} component={"span"}
                                fontWeight={500}
                                whiteSpace={"nowrap"}
                                color={"#000000"}>
                      {t("event.delivered")}: {Moment(order.customValues["delivered_date"]).format("L")}
                    </Typography>
                  </Grid> : null}

              </Grid>

            </Grid>
            <Grid item md={7} xs={12} paddingLeft={{md: 3}} paddingTop={{xs: 3, md: 0}}>
              {order.trackingEvents?.map(
                (trackingEvent, index) => {
                  return <Grid key={`event-${trackingEvent?.description}-${index}`} container columns={12}
                               display={"flex"}
                               marginBottom={4}>
                    <Grid item md={3} xs={3} display={"flex"}
                          flexDirection={"column"}>
                      <Typography p={0}
                                  fontWeight={500}
                                  whiteSpace={"nowrap"}
                                  className={"event-date"}
                      >{Moment(trackingEvent.eventDate).format("L")}</Typography>
                      <Typography p={0} color={"var(--color-alt-content-faded)"}
                                  fontWeight={500}
                                  className={"event-time"}
                                  whiteSpace={"nowrap"}>{Moment(trackingEvent.eventDate).format("LT")}</Typography>
                    </Grid>
                    <Grid item md={9} xs={9} display={"flex"} justifyContent={{md: "space-between", xs: "start"}}
                          flexDirection={{xs: "column-reverse", md: "row"}}>
                      <Typography p={0} fontWeight={500} color={"var(--color-alt-content-faded)!important"}
                                  textAlign={{xs: "end", md: "start"}}
                                  pl={2}>{trackingEvent.location ?? " "}</Typography>
                      <Typography p={0} fontWeight={600}
                                  textAlign={"end"}
                                  color={index === 0 ? "#FF7D00" : "#000000"} pl={2}>
                        {trackingEvent?.customValues &&
                        eventTranslationKey in trackingEvent.customValues &&
                        typeof trackingEvent.customValues[eventTranslationKey] === "string" &&
                        trackingEvent.customValues[eventTranslationKey]
                          ? trackingEvent.customValues[eventTranslationKey]
                          : trackingEvent?.description}
                      </Typography>
                    </Grid>
                  </Grid>;
                })
              }</Grid>
          </Grid>}
          {isNothingFound ?
            <Grid display={"flex"}
                  mt={{md: 8, xs: 3}}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  padding={4}
                  borderRadius={"30px"}
                  border={"1.2px solid var(--color-alt-content-secondary)"}
                  gap={"16px"}
                  alignItems={"center"}
                  textAlign={"center"}>
              <Grid margin={0} width={"54px"} height={"54px"}
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    borderRadius={"50%"}
                    boxShadow={"0 4.8px 4.8px rgba(38, 92, 165, 0.25)"}
                    border={"1.2px solid var(--color-alt-content-secondary)"}
                    sx={{backgroundColor: "var(--color-content-bg)!important"}}>
                <img
                  width="16" src={"https://trtshopping.com/wp-content/uploads/2023/06/Icon.svg"}
                  alt={""} />
              </Grid>
              <h3>{t("nothingFound.title")}</h3>
              <h4>{t("nothingFound.description")}</h4>
            </Grid> : null}
          {isError ?
            <Grid display={"flex"}
                  mt={{md: 8, xs: 3}}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  padding={4}
                  borderRadius={"30px"}
                  border={"1.2px solid var(--color-alt-content-secondary)"}
                  gap={"16px"}
                  alignItems={"center"}
                  textAlign={"center"}>
              <Grid margin={0} width={"54px"} height={"54px"}
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    borderRadius={"50%"}
                    boxShadow={"0 4.8px 4.8px rgba(38, 92, 165, 0.25)"}
                    border={"1.2px solid var(--color-alt-content-secondary)"}
                    sx={{backgroundColor: "var(--color-content-bg)!important"}}>
                <img
                  width="26" src={"https://trtshopping.com/wp-content/uploads/2023/10/warning-error-svgrepo-com.svg"}
                  alt={""} />
              </Grid>
              <h3>{t("error.title")}</h3>
              <h4>{t("error.description")}</h4>
            </Grid> : null}
        </Grid>
      </Grow>
    </Grid>
  );
}

export default Tracking;
